import React, { useEffect, useMemo, useState } from 'react';
import { ArrowUp, ArrowDown } from 'lucide-react';
import AdminLayout from '../../../components/layout/AdminLayout';
import Card from '../../../components/common/Card';
import Button from '../../../components/common/Button';
import Table, { TableColumn, TableAction } from '../../../components/common/Table';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useCompetitionResultEntrySearch,
  useUpdateCompetitionResultEntryRanks,
  useExportCompetitionResultEntries,
} from '../../../hooks/useCompetitionResultEntryApi';
import { CompetitionResultEntryDTO } from '../../../services/apiClient';
import FileIcons from '../../../components/common/FileIcons';
import { getScoreTemplateColumnGroups } from '../../../utils/scoreTemplateColumns';
import { showAlert } from '../../../utils/sweetAlertUtils';
import { FirearmMakeConstants } from '../../../constants/FirearmMakeConstants';
import { FirearmCaliberConstants } from '../../../constants/FirearmCaliberConstants';

const CompetitionResultEntries: React.FC = () => {
  const { id: competitionResultId, competitionExerciseId } = useParams<{
    id: string;
    competitionExerciseId: string;
  }>();
  const navigate = useNavigate();
  const { search } = useCompetitionResultEntrySearch();
  const { updateRanks, isLoading: isSaving } = useUpdateCompetitionResultEntryRanks();
  const { exportEntries, isLoading: isExporting } = useExportCompetitionResultEntries();
  const [entries, setEntries] = useState<CompetitionResultEntryDTO[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isLoading, setIsLoading] = useState(false);

  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    { label: 'Competition Results', href: '/admin/competitionresults' },
    { label: 'Exercises', href: `/admin/competitionresults/${competitionResultId}/exercises` },
    { label: 'Entries', href: '#' },
  ];

  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));

  const fetchEntries = async () => {
    if (!competitionResultId || !competitionExerciseId) return;
    setIsLoading(true);
    try {
      const response = await search({
        page: currentPage,
        pageSize,
        sortBy: 'Position',
        sortDescending: false,
        filters: { competitionResultId, competitionExerciseId },
      });
      setEntries(response.results);
      setTotalRecords(response.totalCount);
    } catch (error) {
      console.error('Error fetching competition result entries:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchEntries();
  }, [competitionResultId, competitionExerciseId, currentPage, pageSize]);

  const moveEntry = (index: number, direction: -1 | 1) => {
    setEntries((prev) => {
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= prev.length) return prev;

      const next = prev.slice();
      [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
      return next;
    });
  };

  const handleSave = async () => {
    try {
      // Only reorders entries within the currently loaded page for now.
      await updateRanks(
        entries.map((entry, index) => ({
          id: entry.id!,
          position: (currentPage - 1) * pageSize + index + 1,
          rank: (currentPage - 1) * pageSize + index + 1,
        })),
      );
      await showAlert({
        title: 'Saved',
        text: 'Entry ranking has been saved.',
        icon: 'success',
      });
      await fetchEntries();
    } catch (error) {
      console.error('Error saving entry ranks:', error);
      await showAlert({
        title: 'Error',
        text: 'Failed to save entry ranking. Please try again.',
        icon: 'error',
      });
    }
  };

  const handleExport = async () => {
    if (!competitionResultId || !competitionExerciseId) return;
    try {
      const url = await exportEntries({ competitionResultId, competitionExerciseId });
      if (url) {
        window.location.href = url;
      }
    } catch (error) {
      console.error('Error exporting competition result entries:', error);
      await showAlert({
        title: 'Error',
        text: 'Failed to export entries. Please try again.',
        icon: 'error',
      });
    }
  };

  const templateColumnGroups = useMemo(() => {
    const template = entries.find((e) => e.score?.exerciseTemplate)?.score?.exerciseTemplate;
    return template ? getScoreTemplateColumnGroups(template) : [];
  }, [entries]);

  const columns: TableColumn[] = useMemo(() => {
    const base: TableColumn[] = [
      {
        label: 'Firearm',
        key: 'firearm',
        render: (row: CompetitionResultEntryDTO) => {
          const firearm = row.score?.memberFirearm;
          const typeTitle =
            firearm?.firearmSpecific?.title || firearm?.firearmType?.title || '-';
          const calibreTitle =
            firearm?.firearmCaliberId === FirearmCaliberConstants.NOT_ON_LIST
              ? firearm?.caliberOther ?? '-'
              : firearm?.caliber?.title ?? '-';
          const actionTitle = firearm?.firearmAction?.title ?? '-';
          const makeTitle =
            firearm?.firearmMakeId === FirearmMakeConstants.NOT_ON_LIST
              ? firearm?.firearmMakeOther
              : firearm?.firearmMake?.title ?? '-';
          return (
            <div className="flex flex-col">
              <span>T: {typeTitle}</span>
              <span>C: {calibreTitle}</span>
              <span>A: {actionTitle}</span>
              <span>M: {makeTitle}</span>
            </div>
          );
        },
      },
      {
        label: 'ID Number',
        key: 'idNumber',
        render: (row: CompetitionResultEntryDTO) => (
          <span>{row.score?.member?.idPassportNumber}</span>
        ),
      },
      {
        label: 'Rank',
        key: 'rank',
        render: (row: CompetitionResultEntryDTO) => <span>{row.rank ?? '-'}</span>,
      },
      {
        label: 'Initials',
        key: 'initials',
        render: (row: CompetitionResultEntryDTO) => <span>{row.score?.member?.initials}</span>,
      },
      {
        label: 'Surname',
        key: 'surname',
        render: (row: CompetitionResultEntryDTO) => <span>{row.score?.member?.lastName}</span>,
      },
    ];

    // One table column per related group of score/template fields (e.g. TT+TX together,
    // X+Cut X together), each rendered as stacked "Label: value" lines - mirrors the
    // Firearm+Calibre combination above and the Endorsement table's stacked-line pattern.
    const dynamic: TableColumn[] = templateColumnGroups.map((group) => ({
      label: group.header,
      key: group.key,
      render: (row: CompetitionResultEntryDTO) => (
        <div className="flex flex-col">
          {group.columns.map((col) => (
            <span key={col.key}>
              {col.header}: {row.score ? col.getValue(row.score) : ''}
            </span>
          ))}
        </div>
      ),
    }));

    const targets: TableColumn[] = [
      {
        label: 'Targets',
        key: 'targets',
        render: (row: CompetitionResultEntryDTO) => (
          <FileIcons
            files={(row.score?.scoreTargetFiles || []).map((tf, i) => ({
              id: tf.fileRecordId,
              label: `Target ${i + 1}`,
            }))}
          />
        ),
      },
    ];

    return [...base, ...dynamic, ...targets];
  }, [entries, templateColumnGroups, currentPage, pageSize]);

  const exerciseTitle =
    entries[0]?.competitionExercise?.exerciseSpecificTitle ||
    entries[0]?.competitionExercise?.exerciseSpecific?.title ||
    'Exercise';

  return (
    <AdminLayout
      activeMenuItem="/admin/competitionresults"
      breadcrumbs={breadcrumbs}
      allowedRoles={['Admin']}
      requiredPermission="Manage.CompetitionResult.View"
    >
      <Card
        title={`Entries for: ${exerciseTitle}`}
        headerActions={
          <div className="flex gap-2">
            <Button
              variant="secondary"
              onClick={() => navigate(`/admin/competitionresults/${competitionResultId}/exercises`)}
            >
              Back
            </Button>
            <Button variant="secondary" onClick={handleExport} isLoading={isExporting}>
              Export
            </Button>
            <Button onClick={handleSave} isLoading={isSaving}>
              Save
            </Button>
          </div>
        }
      >
        <Table
          columns={columns}
          data={entries}
          actions={{
            extra: (row: CompetitionResultEntryDTO): TableAction[] => {
              const index = entries.findIndex((e) => e.id === row.id);
              const actions: TableAction[] = [];
              if (index > 0) {
                actions.push({
                  label: 'Move Up',
                  icon: <ArrowUp className="w-4 h-4" />,
                  onClick: () => moveEntry(index, -1),
                });
              }
              if (index < entries.length - 1) {
                actions.push({
                  label: 'Move Down',
                  icon: <ArrowDown className="w-4 h-4" />,
                  onClick: () => moveEntry(index, 1),
                });
              }
              return actions;
            },
          }}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
          isLoading={isLoading}
          noDataMessage="No entries found for this exercise."
        />
      </Card>
    </AdminLayout>
  );
};

export default CompetitionResultEntries;
